import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Amplify } from 'aws-amplify';
import { createBrowserHistory } from 'history';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { TrackJS } from 'trackjs';
import { COGNITO_CLIENT_OVERRIDE_STORAGE_KEY } from './auth/cognitoClientOverride';
import { consumePendingEntraLogout } from './auth/entraLogout';
import { getConfig } from './Config';
import AppContainer from './components/AppContainer';
import './styles/layout-tokens.css';
import './i18n';
import Plausible from 'plausible-tracker';

// Amplify.Logger.LOG_LEVEL = 'DEBUG';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: Infinity,
            refetchOnWindowFocus: false,
            retry: false,
        },
    },
});

const container = document.getElementById('root');

void fetch('/remote-app-config.json')
    .then(response => response.json())
    .then(json => {
        const config = getConfig(json);
        const entraLogoutUrl = consumePendingEntraLogout(config);
        if (entraLogoutUrl) {
            window.location.replace(entraLogoutUrl);
            return;
        }
        // For the login via Entra Identity Provider when the user is redirected from the GMG Partner Portal, we store the clientId in session storage
        // before the redirect to the login page. This is required as Amplify needs to be configured with the correct clientId before we attempt to parse the Cognito Hosted UI response after login.
        // The mode should be persisted for the entire user session.
        const userPoolClientIdOverride = sessionStorage.getItem(COGNITO_CLIENT_OVERRIDE_STORAGE_KEY);
        const configuredPartnerPortalClientId = config.partnerPortalUserPoolWebClientId;
        const isDedicatedClientOverrideAllowed = !!config.featureFlags.usePartnerPortalDedicatedCognitoClient
            && !!configuredPartnerPortalClientId;

        if (userPoolClientIdOverride) {
            if (isDedicatedClientOverrideAllowed && userPoolClientIdOverride === configuredPartnerPortalClientId) {
                config.amplify.Auth!.Cognito.userPoolClientId = userPoolClientIdOverride;
            } else {
                sessionStorage.removeItem(COGNITO_CLIENT_OVERRIDE_STORAGE_KEY);
            }
        }

        const plausible = Plausible({
            domain: config.tracking.domain,
            trackLocalhost: true,
        });
        plausible.enableAutoPageviews();
        Amplify.configure(config.amplify);

        config.trackJs && TrackJS.install(config.trackJs);

        // Set up the browser history with the updated location (minus the # sign)
        // see https://stackoverflow.com/questions/51218979/react-router-doesnt-work-in-aws-s3-bucket which refers to https://via.studio/journal/hosting-a-reactjs-app-with-routing-on-aws-s3
        // We check to see if the url is in #!/something format
        const path = (/#!(\/.*)$/.exec(window.location.hash) || [])[1];
        if (path) {
            // If it is, we grab the /something portion of the url and use the browser history API to replace the url.
            createBrowserHistory().replace(path);
        }


        const root = createRoot(container!);
        root.render(<React.StrictMode>
            <QueryClientProvider client={queryClient}>
                <AppContainer
                    config={config}
                    trackEvent={!config.tracking.enabled
                        ? () => { }
                        : event => plausible.trackEvent(event.name, {
                            props: {
                                ...event.additionalProperties ?? {},
                            },
                        })} />
                <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>

        </React.StrictMode>);
    });

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
